// src/features/reporting/pages/inventory/CoRemovalPage.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, MenuItem, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PageHeader from '../../../../components/common/PageHeader';
import { useApi } from '../../../../hooks/api/useApi';
import { inventoryAnalyticsAPI } from '../../services/inventoryAnalytics';
import InfoTip, { withInfo } from '../../../../components/common/InfoTip';
import {
  ChartCard, FilterRow, PeriodSelect, SERIES_COLORS, StatRow, StatTile, formatPercent, periodDates,
} from '../../components/analysisParts';

const WINDOWS = [5, 10, 15, 30];
const MIN_COUNTS = [2, 3, 5, 10];

/** Lift as a number plus a thin bar, so the strongest pairs stand out in the list. */
function LiftCell({ value, max }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', height: '100%' }}>
      <Typography variant="body2" sx={{ width: 40, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        {value.toLocaleString('da-DK', { maximumFractionDigits: 1 })}
      </Typography>
      <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: 'action.hover' }}>
        <Box sx={{ width: `${Math.min(value / max, 1) * 100}%`, height: '100%', borderRadius: 1, bgcolor: SERIES_COLORS[0] }} />
      </Box>
    </Box>
  );
}

export default function CoRemovalPage() {
  const { t } = useTranslation(['reporting', 'common']);
  const navigate = useNavigate();
  const { loading, error, execute, clearError } = useApi();

  const [days, setDays] = useState(180);
  const [windowMinutes, setWindowMinutes] = useState(10);
  const [minCount, setMinCount] = useState(3);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    const response = await execute(() => inventoryAnalyticsAPI.getCoRemoval({
      ...periodDates(days), windowMinutes, minCount,
    }));
    if (response.success) setResult(response.data.data);
  }, [execute, days, windowMinutes, minCount]);

  useEffect(() => { load(); }, [load]);

  const pairs = result?.pairs ?? [];
  const maxLift = Math.max(...pairs.map((pair) => pair.lift), 1);

  const columns = [
    { field: 'item_a_name', headerName: t('reporting:co_removal.columns.item_a'), flex: 1.3 },
    { field: 'item_b_name', headerName: t('reporting:co_removal.columns.item_b'), flex: 1.3 },
    withInfo(
      { field: 'count', headerName: t('reporting:co_removal.columns.count'), flex: 0.7, type: 'number' },
      t('reporting:help.co_removal.count'),
    ),
    withInfo({
      field: 'confidence_a_to_b',
      headerName: t('reporting:co_removal.columns.a_to_b'),
      flex: 0.8,
      type: 'number',
      valueFormatter: (value) => formatPercent(value * 100, 0),
    }, t('reporting:help.co_removal.a_to_b')),
    withInfo({
      field: 'confidence_b_to_a',
      headerName: t('reporting:co_removal.columns.b_to_a'),
      flex: 0.8,
      type: 'number',
      valueFormatter: (value) => formatPercent(value * 100, 0),
    }, t('reporting:help.co_removal.b_to_a')),
    withInfo({
      field: 'lift',
      headerName: t('reporting:co_removal.columns.lift'),
      flex: 1.2,
      renderCell: ({ value }) => <LiftCell value={value} max={maxLift} />,
    }, t('reporting:help.co_removal.lift')),
  ];

  return (
    <Box flex={1} minHeight={0} display="flex" flexDirection="column">
      <PageHeader
        onBack={() => navigate('/reports')}
        title={t('reporting:co_removal.title')}
        subtitle={t('reporting:co_removal.subtitle')}
      />

      <FilterRow>
        <PeriodSelect value={days} onChange={setDays} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select size="small" label={t('reporting:co_removal.window')} value={windowMinutes}
            onChange={(e) => setWindowMinutes(Number(e.target.value))} sx={{ minWidth: 220 }}
          >
            {WINDOWS.map((minutes) => (
              <MenuItem key={minutes} value={minutes}>{t('reporting:co_removal.minutes', { count: minutes })}</MenuItem>
            ))}
          </TextField>
          <InfoTip title={t('reporting:help.co_removal.window')} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select size="small" label={t('reporting:co_removal.min_count')} value={minCount}
            onChange={(e) => setMinCount(Number(e.target.value))} sx={{ minWidth: 180 }}
          >
            {MIN_COUNTS.map((count) => (
              <MenuItem key={count} value={count}>{t('reporting:co_removal.times', { count })}</MenuItem>
            ))}
          </TextField>
          <InfoTip title={t('reporting:help.co_removal.min_count')} />
        </Box>
      </FilterRow>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>{error}</Alert>}

      <StatRow>
        <StatTile
          loading={loading && !result}
          label={t('reporting:co_removal.baskets')}
          value={result ? result.basket_count.toLocaleString('da-DK') : '—'}
          caption={t('reporting:co_removal.baskets_caption', { count: windowMinutes })}
          info={t('reporting:help.co_removal.baskets')}
        />
        <StatTile
          loading={loading && !result}
          label={t('reporting:co_removal.multi_baskets')}
          value={result ? result.multi_item_basket_count.toLocaleString('da-DK') : '—'}
          caption={result && result.basket_count
            ? t('reporting:co_removal.multi_caption', { share: formatPercent(result.multi_item_basket_count / result.basket_count * 100, 0) })
            : null}
          info={t('reporting:help.co_removal.multi_baskets')}
        />
        <StatTile
          loading={loading && !result}
          label={t('reporting:co_removal.pairs')}
          value={result ? pairs.length.toLocaleString('da-DK') : '—'}
          caption={t('reporting:co_removal.pairs_caption', { count: minCount })}
          info={t('reporting:help.co_removal.pairs')}
        />
      </StatRow>

      <ChartCard title={t('reporting:co_removal.table_title')} description={t('reporting:co_removal.table_description')}>
        <DataGrid
          rows={pairs}
          getRowId={(row) => `${row.item_a_id}-${row.item_b_id}`}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          localeText={{ noRowsLabel: t('reporting:co_removal.empty') }}
          autoHeight
          sx={{ border: 'none', '& .MuiDataGrid-cell': { alignItems: 'center', fontVariantNumeric: 'tabular-nums' } }}
        />
      </ChartCard>

      <Typography variant="body2" color="text.secondary">{t('reporting:co_removal.explanation')}</Typography>
    </Box>
  );
}
